import { type ReactNode } from 'react';
import { Input } from '@/components/ui/input';
import { SegmentedToggle } from '@/components/ui/segmented-toggle';
import { useCreatorFilters } from '../hooks/use-creator-filters';
import { useAuth } from '@/hooks/use-auth';
import { FilterSelects } from './filter-selects';
import { CollectorInput, FilterActions } from './creators-filter-actions';

// DEBOOK-1900: "Papelera" y el dropdown "Alertas"
// (`./alerted-inactive-filter.tsx`) se ocultan de la UI porque ya no se
// usan. El resto del código sigue vivo — la vista `trash` en
// `buildBaseFilter`/`buildSort`/`expand`, el tipo `AlertedFilter` y
// `alertedClause()` — para reactivarlos basta con volver a añadir la opción
// aquí abajo y volver a renderizar <AlertedInactiveFilter /> en
// CreatorsFilters.
const VIEW_OPTIONS = [
  { value: 'active' as const, label: 'Activos' },
  { value: 'alerted' as const, label: 'Alertados' },
];

function SearchInput({
  value,
  onChange,
}: {
  value: string;
  onChange: (v: string) => void;
}): ReactNode {
  return (
    <Input
      placeholder="Buscar..."
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full sm:!w-36"
    />
  );
}

type FState = ReturnType<typeof useCreatorFilters.getState>;

function isDirty(f: FState): boolean {
  return !!(
    f.category ||
    f.creator_type ||
    f.rank ||
    f.collector ||
    f.status ||
    f.sex ||
    f.search ||
    f.alertedFilter
  );
}

function FilterSelectsBound({ f }: { f: FState }): ReactNode {
  return (
    <FilterSelects
      category={f.category}
      creator_type={f.creator_type}
      rank={f.rank}
      status={f.status}
      sex={f.sex}
      onCategorySel={f.setCategorySel}
      onRank={f.setRank}
      onStatus={f.setStatus}
      onSex={f.setSex}
    />
  );
}

function ViewToggles({ f }: { f: FState }): ReactNode {
  return (
    <SegmentedToggle
      options={VIEW_OPTIONS}
      value={f.view}
      onChange={f.setView}
    />
  );
}

export function CreatorsFilters(): ReactNode {
  const f = useCreatorFilters();
  const { isAdmin } = useAuth();
  return (
    <div className="flex flex-wrap items-center gap-sm">
      <ViewToggles f={f} />
      <SearchInput value={f.search} onChange={f.setSearch} />
      <FilterSelectsBound f={f} />
      {isAdmin && (
        <CollectorInput value={f.collector ?? ''} onChange={f.setCollector} />
      )}
      <FilterActions dirty={isDirty(f)} onClear={f.clearFilters} />
    </div>
  );
}
